/**
 * #109 · Clientes de XYVONA.
 *
 * El listado con lo que hace falta para decidir —cuánto compraron, cuánto
 * activaron, cuándo fue el último escaneo— y el alta de una marca con su
 * primer administrador.
 *
 * Dos cosas que la pantalla tiene que dejar claras porque el backend no puede:
 *
 * 1. **La contraseña temporal se ve UNA vez.** Se muestra en un diálogo
 *    aparte, con botón de copiar y el aviso de que no se puede volver a
 *    consultar. Si se pierde, se resetea.
 * 2. **Suspender no apaga la verificación.** Un admin que suspende a un
 *    cliente moroso tiene que saber que las etiquetas ya impresas se siguen
 *    verificando como genuinas — el consumidor no tiene por qué enterarse de
 *    una disputa comercial mirando un producto legítimo.
 *
 * Suspender y reactivar pasan por el mismo diálogo, con motivo obligatorio
 * (#171): las dos operaciones quedan en la auditoría y reactivar devuelve el
 * acceso a todos los usuarios de la marca, que es tan consecuente como
 * sacárselo. El botón de la fila dice lo que va a pasar según el estado, y
 * el rojo queda para el diálogo de suspensión, no para la tabla.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { MIN_REASON } from '../labels/units';
import { ClientForm, type ClientDraft } from './ClientForm';
import { hqKeys } from './keys';

type Tenant = Schemas['TenantOut'];
type Created = Schemas['TenantCreatedOut'];

export function ClientsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [creando, setCreando] = useState(false);
  const [creado, setCreado] = useState<Created | null>(null);
  const [cambiando, setCambiando] = useState<Tenant | null>(null);
  const [motivo, setMotivo] = useState('');
  const motivoListo = motivo.trim().length >= MIN_REASON;

  const clientes = useApiQuery<Tenant[]>(hqKeys.clients(''), '/api/v1/backoffice/tenants');

  const alta = useMutation<Created, unknown, ClientDraft>({
    mutationFn: (draft) => api.post<Created>('/api/v1/backoffice/tenants', draft),
    onSuccess: (respuesta) => {
      setCreando(false);
      setCreado(respuesta);
      void queryClient.invalidateQueries({ queryKey: hqKeys.all });
    },
  });

  const cambiarEstado = useMutation<Tenant, unknown, { tenant: Tenant; activo: boolean }>({
    mutationFn: ({ tenant, activo }) =>
      api.patch<Tenant>(`/api/v1/backoffice/tenants/${tenant.tenant_id}`, {
        is_active: activo,
        reason: motivo.trim(),
      }),
    onSuccess: () => {
      setCambiando(null);
      setMotivo('');
      void queryClient.invalidateQueries({ queryKey: hqKeys.all });
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <Card>
        <CardHeader
          title={t('clients.title')}
          description={t('clients.description')}
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                alta.reset();
                setCreando(true);
              }}
            >
              {t('clients.new')}
            </Button>
          }
        />
        <CardBody className="p-0">
          <QueryBoundary query={clientes} empty={{ title: t('clients.empty') }}>
            {(filas) => (
              <Table caption={t('clients.title')}>
                <thead>
                  <tr>
                    <Th>{t('clients.col.client')}</Th>
                    <Th>{t('clients.col.industry')}</Th>
                    <Th className="text-right">{t('clients.col.units')}</Th>
                    <Th className="text-right">{t('clients.col.activated')}</Th>
                    <Th className="text-right">{t('clients.col.users')}</Th>
                    <Th>{t('clients.col.lastScan')}</Th>
                    <Th>{t('clients.col.state')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => (
                    <tr key={fila.tenant_id}>
                      <Td>
                        <span className="font-medium text-ink">{fila.legal_name}</span>
                        <span className="block text-xs text-ink-muted">{fila.slug}</span>
                      </Td>
                      <Td>{fila.industry}</Td>
                      <Td className="text-right tabular-nums">{fmt.number(fila.units)}</Td>
                      <Td className="text-right tabular-nums">
                        {fmt.number(fila.units_activated)}
                      </Td>
                      <Td className="text-right tabular-nums">{fmt.number(fila.users)}</Td>
                      <Td className="text-xs text-ink-muted">
                        {fila.last_scan_at === null ? '—' : fmt.date(fila.last_scan_at)}
                      </Td>
                      <Td>
                        <Badge tone={fila.is_active ? 'neutral' : 'critical'}>
                          {t(fila.is_active ? 'clients.state.active' : 'clients.state.suspended')}
                        </Badge>
                      </Td>
                      <Td className="text-right">
                        <Button
                          size="sm"
                          variant={fila.is_active ? 'ghost' : 'secondary'}
                          onClick={() => {
                            cambiarEstado.reset();
                            setMotivo('');
                            setCambiando(fila);
                          }}
                        >
                          {t(fila.is_active ? 'clients.suspend' : 'clients.reactivate')}
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </QueryBoundary>
        </CardBody>
      </Card>

      <Dialog
        open={creando}
        onClose={() => { setCreando(false); }}
        title={t('clients.new')}
      >
        <ClientForm
          pending={alta.isPending}
          error={alta.error}
          onSubmit={(draft) => { alta.mutate(draft); }}
          onCancel={() => { setCreando(false); }}
        />
      </Dialog>

      {/* La temporal, una sola vez. */}
      <Dialog
        open={creado !== null}
        onClose={() => { setCreado(null); }}
        title={t('clients.created.title')}
        description={creado?.slug}
        footer={
          <Button variant="primary" size="sm" onClick={() => { setCreado(null); }}>
            {t('hq.close')}
          </Button>
        }
      >
        {creado?.temp_password !== null && creado?.temp_password !== undefined && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink-muted">{creado.admin_email}</p>
            <label className="text-xs font-medium text-ink-subtle uppercase">
              {t('clients.created.password')}
            </label>
            <code className="rounded border border-border bg-surface-sunken px-3 py-2 font-mono text-sm break-all">
              {creado.temp_password}
            </code>
            <p className="text-sm text-ink-muted">{t('clients.created.once')}</p>
          </div>
        )}
      </Dialog>

      <Dialog
        open={cambiando !== null}
        onClose={() => { setCambiando(null); }}
        title={t(cambiando?.is_active === false ? 'clients.reactivate' : 'clients.suspend')}
        description={cambiando?.legal_name}
        tone={cambiando?.is_active === false ? 'neutral' : 'critical'}
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => { setCambiando(null); }}>
              {t('form.cancel')}
            </Button>
            <Button
              size="sm"
              variant={cambiando?.is_active === false ? 'primary' : 'critical'}
              disabled={cambiarEstado.isPending || !motivoListo}
              onClick={() => {
                if (cambiando !== null) {
                  cambiarEstado.mutate({ tenant: cambiando, activo: !cambiando.is_active });
                }
              }}
            >
              {t(cambiando?.is_active === false ? 'clients.reactivate' : 'clients.suspend')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">
            {t(cambiando?.is_active === false ? 'clients.reactivate.confirm' : 'clients.suspend.confirm')}
          </p>
          <label className="flex flex-col gap-1 text-sm">
            {t('clients.suspend.reason')}
            <input
              className="rounded border border-border px-3 py-2 text-sm"
              value={motivo}
              onChange={(event) => { setMotivo(event.target.value); }}
            />
            {!motivoListo && (
              <span className="text-xs text-ink-subtle">
                {t('clients.suspend.reasonHint', { min: String(MIN_REASON) })}
              </span>
            )}
          </label>
          {cambiarEstado.isError && (
            <p className="text-sm text-critical">{t('clients.suspend.failed')}</p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
